import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useBranches } from '@/queries/useBranches'
import { useBranchesForRestaurant } from '@/queries/useBranchesForRestaurant'
import { useRestaurants } from '@/queries/useRestaurants'
import { useSaveUserPreferences } from '@/hooks/useUserPreferences'
import { Building2, MapPin, Settings } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DashboardContextSettings } from './DashboardContextSettings'

export type UserRole = 'branch_manager' | 'restaurant_admin' | 'super_admin'

interface DashboardContextProps {
  role: UserRole
  restaurantId: string
  restaurantName: string
  branchId?: string
  branchName?: string
  onRestaurantChange?: (restaurantId: string | null) => void
  onBranchChange?: (branchId: string | null) => void
  isLoading?: boolean
  error?: Error | null
  compact?: boolean // For sidebar display
}

// Helper to check if restaurantId is valid (not empty or 'loading')
const isValidRestaurantId = (id: string): boolean => {
  return id !== '' && id !== 'loading'
}

// Helper to check if we should show restaurant info (has valid name)
const shouldShowRestaurant = (id: string, name: string): boolean => {
  // Show if we have a valid restaurant name (not placeholder text)
  const hasValidName = name && name !== 'Loading...' && name !== 'Restaurant'
  // Or if we have a valid ID
  return hasValidName || isValidRestaurantId(id)
}

export default function DashboardContext({
  role,
  restaurantId,
  restaurantName,
  branchId,
  branchName,
  onRestaurantChange,
  onBranchChange,
  isLoading = false,
  error = null,
  compact = false,
}: DashboardContextProps) {
  const { t } = useTranslation('admin')
  // Settings state - default to showing both restaurant and branch
  const [showRestaurant, setShowRestaurant] = useState(true)
  const [showBranch, setShowBranch] = useState(true)
  const [settingsOpen, setSettingsOpen] = useState(false)

  // Log when props change (for debugging)
  console.log('[DashboardContext] Props updated:', {
    role,
    restaurantId,
    restaurantName,
    branchId,
    branchName,
    isLoading,
  })

  // Hook to save preferences to database
  const { mutateAsync: saveUserPreferences } = useSaveUserPreferences()
  // Fetch restaurants for super admin
  const {
    data: restaurants,
    isLoading: isLoadingRestaurants,
  } = useRestaurants()

  // Fetch branches for restaurant admin (using existing useBranches hook)
  // Only enable for restaurant admin role
  const {
    branches: { data: branchesForRestaurantAdmin, isLoading: isLoadingBranchesForAdmin },
  } = useBranches(role === 'restaurant_admin')

  // Fetch branches for super admin (for selected restaurant)
  // Only fetch if restaurantId is valid (not empty, not 'loading')
  const shouldFetchBranches = role === 'super_admin' && isValidRestaurantId(restaurantId)
  const {
    data: branchesForSuperAdmin,
    isLoading: isLoadingBranchesForSuperAdmin,
  } = useBranchesForRestaurant(
    shouldFetchBranches ? restaurantId : null
  )

  console.log('[DashboardContext] Branches for super admin:', {
    shouldFetchBranches,
    restaurantId,
    branchesForSuperAdmin,
    isLoadingBranchesForSuperAdmin,
  })

  // Determine which branches to use based on role
  // Ensure we always return an array (not undefined)
  const branches =
    role === 'super_admin'
      ? branchesForSuperAdmin || []
      : role === 'restaurant_admin'
      ? branchesForRestaurantAdmin || []
      : []

  const isLoadingBranches =
    role === 'super_admin'
      ? isLoadingBranchesForSuperAdmin
      : role === 'restaurant_admin'
      ? isLoadingBranchesForAdmin
      : false

  // Handler for branch selection that saves to database
  const handleBranchChangeWithSave = async (value: string) => {
    const newBranchId = value === 'all' ? null : value
    console.log('[DashboardContext] Branch changed:', newBranchId)

    // Update parent state first for immediate UI feedback
    onBranchChange?.(newBranchId)

    // Save to database
    try {
      console.log('[DashboardContext] Saving branch preference...')
      await saveUserPreferences({
        restaurantId: restaurantId || undefined,
        branchId: newBranchId || undefined,
      })
      console.log('[DashboardContext] Branch preference saved successfully')
    } catch (error) {
      console.error('[DashboardContext] Failed to save branch preference:', error)
      // Don't show error to user - the UI is already updated
    }
  }

  // Handler for restaurant selection that saves to database
  const handleRestaurantChangeWithSave = async (value: string) => {
    const newRestaurantId = value === 'all' ? null : value
    console.log('[DashboardContext] Restaurant changed:', newRestaurantId)

    // Update parent state first for immediate UI feedback
    onRestaurantChange?.(newRestaurantId)
    // Clear branch when restaurant changes
    onBranchChange?.(null)

    // Save to database
    try {
      console.log('[DashboardContext] Saving restaurant preference...')
      await saveUserPreferences({
        restaurantId: newRestaurantId || undefined,
        branchId: undefined, // Clear branch when restaurant changes
      })
      console.log('[DashboardContext] Restaurant preference saved successfully')
    } catch (error) {
      console.error('[DashboardContext] Failed to save restaurant preference:', error)
      // Don't show error to user - the UI is already updated
    }
  }

  // Branch Manager: Read-only display (no settings button)
  if (role === 'branch_manager') {
    if (compact) {
      return (
        <div className="space-y-3" data-testid="dashboard-context-branch-manager">
          {/* Restaurant Display */}
          <div>
            <div className="flex items-center gap-1.5 mb-1.5">
              <Building2 className="h-3.5 w-3.5 text-[var(--text-secondary)]" />
              <p className="text-xs text-[var(--text-secondary)]">{t('context.restaurant')}</p>
            </div>
            {isLoading || !shouldShowRestaurant(restaurantId, restaurantName) ? (
              <Skeleton className="h-4 w-24" />
            ) : (
              <p className="text-sm font-semibold text-[var(--text-primary)] truncate">
                {restaurantName}
              </p>
            )}
          </div>

          {/* Branch Display */}
          <div>
            <div className="flex items-center gap-1.5 mb-1.5">
              <MapPin className="h-3.5 w-3.5 text-[var(--text-secondary)]" />
              <p className="text-xs text-[var(--text-secondary)]">{t('context.branch')}</p>
            </div>
            {branchName ? (
              <p className="text-sm font-semibold text-[var(--text-primary)] truncate">
                {branchName}
              </p>
            ) : (
              <Skeleton className="h-4 w-24" />
            )}
          </div>
        </div>
      )
    }
    return (
      <Card className="mb-6" data-testid="dashboard-context-branch-manager">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-[var(--text-secondary)]" />
              <div>
                <p className="text-sm text-[var(--text-secondary)]">{t('context.restaurant')}</p>
                <p className="font-semibold text-[var(--text-primary)]">
                  {isLoading ? <Skeleton className="h-5 w-32" /> : restaurantName}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-[var(--text-secondary)]" />
              <div>
                <p className="text-sm text-[var(--text-secondary)]">{t('context.branch')}</p>
                {branchName ? (
                  <p className="font-semibold text-[var(--text-primary)]">
                    {branchName}
                  </p>
                ) : (
                  <Skeleton className="h-5 w-32" />
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  // Restaurant Admin: Read-only restaurant, branch selector (can switch branches)
  if (role === 'restaurant_admin') {
    if (compact) {
      return (
        <div className="space-y-3" data-testid="dashboard-context-restaurant-admin">
          {/* Restaurant Display */}
          {showRestaurant && (
            <div>
              <div className="flex items-center justify-between gap-1.5 mb-1.5">
                <div className="flex items-center gap-1.5">
                  <Building2 className="h-3.5 w-3.5 text-[var(--text-secondary)]" />
                  <p className="text-xs text-[var(--text-secondary)]">{t('context.restaurant')}</p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5"
                  onClick={() => setSettingsOpen(true)}
                  title={t('context.settings', { defaultValue: 'Settings' })}
                >
                  <Settings className="h-3.5 w-3.5" />
                </Button>
              </div>
              {isLoading || !shouldShowRestaurant(restaurantId, restaurantName) ? (
                <Skeleton className="h-4 w-24" />
              ) : (
                <p className="text-sm font-semibold text-[var(--text-primary)] truncate">
                  {restaurantName}
                </p>
              )}
            </div>
          )}

          {/* Branch Display - Read-only */}
          {showBranch && (
            <div>
              <div className="flex items-center gap-1.5 mb-1.5">
                <MapPin className="h-3.5 w-3.5 text-[var(--text-secondary)]" />
                <p className="text-xs text-[var(--text-secondary)]">{t('context.branch')}</p>
              </div>
              {branchName ? (
                <p className="text-sm font-semibold text-[var(--text-primary)] truncate">
                  {branchName}
                </p>
              ) : (
                <Skeleton className="h-4 w-24" />
              )}
            </div>
          )}
          {error && (
            <Alert variant="destructive" className="mt-2">
              <AlertDescription className="text-xs">{error.message}</AlertDescription>
            </Alert>
          )}

          {/* Settings Dialog */}
          <DashboardContextSettings
            role={role}
            open={settingsOpen}
            onOpenChange={setSettingsOpen}
            showRestaurant={showRestaurant}
            showBranch={showBranch}
            onShowRestaurantChange={setShowRestaurant}
            onShowBranchChange={setShowBranch}
            restaurantId={restaurantId}
            restaurantName={restaurantName}
            branchId={branchId}
            branchName={branchName}
            branches={branches}
            isLoadingBranches={isLoadingBranches}
            onRestaurantChange={onRestaurantChange}
            onBranchChange={onBranchChange}
          />
        </div>
      )
    }
    return (
      <Card className="mb-6" data-testid="dashboard-context-restaurant-admin">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            <div className="flex items-center gap-2 flex-1">
              <Building2 className="h-5 w-5 text-[var(--text-secondary)]" />
              <div className="flex-1">
                <p className="text-sm text-[var(--text-secondary)]">{t('context.restaurant')}</p>
                <p className="font-semibold text-[var(--text-primary)]">
                  {isLoading ? <Skeleton className="h-5 w-32" /> : restaurantName}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-1 sm:max-w-xs">
              <MapPin className="h-5 w-5 text-[var(--text-secondary)]" />
              <div className="flex-1">
                <p className="text-sm text-[var(--text-secondary)] mb-1">{t('context.branch')}</p>
                {isLoadingBranches ? (
                  <Skeleton className="h-10 w-full" />
                ) : (
                  <Select
                    value={branchId || ''}
                    onValueChange={handleBranchChangeWithSave}
                    disabled={isLoading}
                  >
                    <SelectTrigger
                      className="w-full"
                      aria-label="Select branch"
                    >
                      <SelectValue placeholder={t('context.selectBranch')} />
                    </SelectTrigger>
                    <SelectContent>
                      {branches && branches.length > 0 ? (
                        branches.map((branch) => (
                          <SelectItem key={branch.id} value={branch.id}>
                            {branch.name}
                            {branch.address && ` - ${branch.address}`}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="no-branches" disabled>
                          {t('context.noBranchesAvailable')}
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
          </div>
          {error && (
            <Alert variant="destructive" className="mt-4">
              <AlertDescription>{error.message}</AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>
    )
  }

  // Super Admin: Restaurant and branch selectors (can switch both)
  if (compact) {
    return (
      <div className="space-y-3" data-testid="dashboard-context-super-admin">
        {/* Restaurant Display - Read-only */}
        {showRestaurant && (
          <div>
            <div className="flex items-center justify-between gap-1.5 mb-1.5">
              <div className="flex items-center gap-1.5">
                <Building2 className="h-3.5 w-3.5 text-[var(--text-secondary)]" />
                <p className="text-xs text-[var(--text-secondary)]">{t('context.restaurant')}</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5"
                onClick={() => setSettingsOpen(true)}
                title={t('context.settings', { defaultValue: 'Settings' })}
              >
                <Settings className="h-3.5 w-3.5" />
              </Button>
            </div>
            {isLoading || !shouldShowRestaurant(restaurantId, restaurantName) ? (
              <Skeleton className="h-4 w-24" />
            ) : (
              <p className="text-sm font-semibold text-[var(--text-primary)] truncate">
                {restaurantName || t('context.allRestaurants')}
              </p>
            )}
          </div>
        )}

        {/* Branch Display - Read-only */}
        {showBranch && (
          <div>
            <div className="flex items-center gap-1.5 mb-1.5">
              <MapPin className="h-3.5 w-3.5 text-[var(--text-secondary)]" />
              <p className="text-xs text-[var(--text-secondary)]">{t('context.branch')}</p>
            </div>
            {branchName ? (
              <p className="text-sm font-semibold text-[var(--text-primary)] truncate">
                {branchName}
              </p>
            ) : (
              <Skeleton className="h-4 w-24" />
            )}
          </div>
        )}

        {error && (
          <Alert variant="destructive" className="mt-2">
            <AlertDescription className="text-xs">{error.message}</AlertDescription>
          </Alert>
        )}

        {/* Settings Dialog */}
        <DashboardContextSettings
          role={role}
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
          showRestaurant={showRestaurant}
          showBranch={showBranch}
          onShowRestaurantChange={setShowRestaurant}
          onShowBranchChange={setShowBranch}
          restaurantId={restaurantId}
          restaurantName={restaurantName}
          branchId={branchId}
          branchName={branchName}
          restaurants={restaurants}
          branches={branches}
          isLoadingRestaurants={isLoadingRestaurants}
          isLoadingBranches={isLoadingBranches || isLoading}
          onRestaurantChange={onRestaurantChange}
          onBranchChange={onBranchChange}
        />
      </div>
    )
  }
  return (
    <Card className="mb-6" data-testid="dashboard-context-super-admin">
      <CardContent className="pt-6">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          <div className="flex items-center gap-2 flex-1">
            <Building2 className="h-5 w-5 text-[var(--text-secondary)]" />
            <div className="flex-1">
                <p className="text-sm text-[var(--text-secondary)] mb-1">{t('context.restaurant')}</p>
              {isLoadingRestaurants ? (
                <Skeleton className="h-10 w-full" />
              ) : (
                <Select
                  value={restaurantId || ''}
                  onValueChange={handleRestaurantChangeWithSave}
                  disabled={isLoading}
                >
                  <SelectTrigger
                    className="w-full"
                    aria-label="Select restaurant"
                  >
                    <SelectValue placeholder={t('context.selectRestaurant')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('context.allRestaurants')}</SelectItem>
                    {restaurants && restaurants.length > 0 ? (
                      restaurants.map((restaurant) => (
                        <SelectItem key={restaurant.id} value={restaurant.id}>
                          {restaurant.name}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="no-restaurants" disabled>
                        {t('context.noRestaurantsAvailable')}
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-1 sm:max-w-xs">
            <MapPin className="h-5 w-5 text-[var(--text-secondary)]" />
            <div className="flex-1">
              <p className="text-sm text-[var(--text-secondary)] mb-1">{t('context.branch')}</p>
              {isLoadingBranches ? (
                <Skeleton className="h-10 w-full" />
              ) : (
                <Select
                  value={branchId || 'all'}
                  onValueChange={handleBranchChangeWithSave}
                  disabled={isLoading || !restaurantId || restaurantId === 'all'}
                >
                  <SelectTrigger
                    className="w-full"
                    aria-label="Select branch"
                  >
                    <SelectValue placeholder={t('context.selectBranch')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('context.allBranches')}</SelectItem>
                    {branches && branches.length > 0 ? (
                      branches.map((branch) => (
                        <SelectItem key={branch.id} value={branch.id}>
                          {branch.name}
                          {branch.address && ` - ${branch.address}`}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="no-branches" disabled>
                        {restaurantId && restaurantId !== 'all'
                          ? t('context.noBranchesAvailable')
                          : t('context.selectRestaurantFirst')}
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        </div>
        {error && (
          <Alert variant="destructive" className="mt-4">
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  )
}

